/**
 * Preview wrapper over the shared section-JS facade (section-js-facade.js).
 * Thin by design so preview scoping can never diverge from export scoping.
 */

import {
  buildFacadeIIFE,
  escapeForInlineScript,
  sectionUsesDocumentScope
} from '../shared/section-js-facade.js';
import { behaviourIdsInHtml } from '../shared/behaviour-contract.js';
import {
  assembleBehaviourJS,
  behaviourBindJS,
  behaviourRuntimeLoaded
} from '../behaviours/behaviour-loader.js';

export { escapeForInlineScript };

export function generateScopedScript(sectionJs, sectionId, instanceId, section = null) {
  // AG-4: `loadedOnly`. The canvas must never fail to render because a runtime
  // did not arrive — it degrades to the captured static state and the LOADER
  // reports the miss through reportError (see iframe-renderer's
  // assembleDocumentWarmed). An export makes the opposite choice, from the same
  // builder, because an export is a shipped artifact and a preview is a view.
  const bindJS = section ? behaviourBindJS(section.html, { loadedOnly: true }) : '';
  if (!sectionJs && !bindJS) return '';
  return buildFacadeIIFE({
    mode: 'preview',
    js: `${sectionJs || ''}${bindJS}`,
    sectionId,
    instanceId,
    documentScope: sectionUsesDocumentScope(section || { sectionId })
  });
}

function isParseableSectionScript(sectionJs) {
  try {
    // Matches the preview wrapper semantics: section JS runs as a function body,
    // so top-level `return` remains valid while incomplete JS is caught before
    // it can poison the iframe's whole inline script block.
    new Function(sectionJs);
    return true;
  } catch (err) {
    return err;
  }
}

export function generateAllSectionScripts(sections) {
  const scripts = [];
  for (const section of sections) {
    const bindsBehaviour = behaviourBindJS(section.html, { loadedOnly: true }) !== '';
    if (!section.js && !bindsBehaviour) continue;
    if (section.js) {
      const parseError = isParseableSectionScript(section.js);
      if (parseError !== true) {
        console.warn(
          '[PB] Skipping malformed section JS in preview:',
          section.sectionId,
          section.instanceId,
          parseError.message
        );
        // A broken hand-written script must not take the section's BEHAVIOUR
        // down with it: the two have different authors and different failure
        // modes. Fall through with the js dropped and the stamp honoured.
        if (!bindsBehaviour) continue;
        scripts.push(generateScopedScript('', section.sectionId, section.instanceId, section));
        continue;
      }
    }
    scripts.push(
      generateScopedScript(section.js || '', section.sectionId, section.instanceId, section)
    );
  }
  const bundle = assembleLoadedBehaviourJS(sections);
  if (bundle) scripts.unshift(bundle);
  return scripts.join('\n');
}

/**
 * The prelude + every runtime the given sections stamp AND that actually
 * loaded. Missing ones are simply absent, which is what makes the canvas
 * degrade to static rather than throw.
 */
function assembleLoadedBehaviourJS(sections) {
  const ids = new Set();
  for (const section of sections || []) {
    for (const id of behaviourIdsInHtml(section?.html)) {
      if (behaviourRuntimeLoaded(id)) ids.add(id);
    }
  }
  return ids.size ? assembleBehaviourJS([...ids]) : '';
}

/**
 * Strict guest parents cannot call new Function, even for syntax validation.
 * Keep each existing facade in its own parser unit: the frame reports a bad
 * script natively, while later section scripts remain able to execute.
 */
export function generateSectionScriptTags(sections) {
  const tags = [];
  // AG-4: the bundle is its own unit, first, for the same reason the per-section
  // facades are separate units — a strict guest frame reports a bad script
  // natively, and one bad unit must not take the others down.
  const bundle = assembleLoadedBehaviourJS(sections);
  if (bundle) tags.push(`<script>${escapeForInlineScript(bundle)}</script>`);
  for (const section of sections) {
    const scoped = generateScopedScript(
      section.js || '',
      section.sectionId,
      section.instanceId,
      section
    );
    if (scoped) tags.push(`<script>${scoped}</script>`);
  }
  return tags.join('\n');
}
